import React, { cloneElement, forwardRef, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Tooltip from '../Tooltip';

import brand from './Icons/brand';
import regular from './Icons/regular';
import solid from './Icons/solid';
import thin from './Icons/thin';

import styles from './Icon.module.scss';

const sizeIcon = {
  small: {
    container: 22,
    icon: 14
  },
  medium: {
    container: 32,
    icon: 16
  },
  big: {
    container: 40,
    icon: 20
  },
  huge: {
    container: 40,
    icon: 24
  }
}

const Icon = forwardRef(({
  id,
  onClick,
  onKeyPress,
  isButton,
  className,
  label,
  width,
  style,
  theme,
  fill,
  size,
  disabled,
  active,
  activeFill,
  tooltip,
  tooltipPosition,
  ...rest
}, ref) => {
  const cnIcon = cn(styles.icon, className, {
    [styles.isButton]: isButton,
    [styles.disabled]: disabled,
    [styles.alignComment]: label === 'comment-alt' || label === 'comment-alt-plus',
    [styles.alignTag]: label === 'tag'
  });

  const containerStyle = useMemo(
    () => {
      const initSize = !isButton && width
        ? width
        : isButton
          ? sizeIcon[size].container
          : sizeIcon[size].icon;
      return {
        width: initSize,
        height: initSize,
        flex: `0 0 ${initSize}px`,
        ...style
      }
    },
    [isButton, size, width, style]
  );

  const getBrandColor = useMemo(
    () => {
      switch (label) {
        case 'instagram-color': return '#AC35A9';
        case 'facebook-color': return '#3B5998';
        case 'tiktok-color': return '#010101';
        case 'twitter-color': return '#1DA1F2';
        case 'youtube-color': return '#E62117';
        default: return fill;
      }
    },
    [fill, label]
  );

  const IconStyle = useMemo(
    () => {
      const initSize = (!isButton && width) ? width : sizeIcon[size].icon;
      return {
        fill: !active ? getBrandColor : activeFill,
        width: initSize,
        height: initSize,
        opacity: disabled ? '0.4' : '1',
      }
    },
    [isButton, size, width, disabled, getBrandColor]
  )

  const getIcon = useMemo(
    () => {
      switch (theme) {
        case 'brand': return brand()[label];
        case 'regular': return regular()[label];
        case 'solid': return solid()[label];
        case 'thin': return thin()[label];
        default: return thin()['info'];
      }
    },
    [theme, label]
  );

  const renderIcon = () => label && (
    <span
      id={id}
      ref={ref}
      className={cnIcon}
      style={containerStyle}
      onClick={disabled ? null : onClick}
      onKeyPress={onKeyPress}
      role={onClick ? 'button' : 'img'}
      {...rest}
    >
      {cloneElement(
        !active
          ? getIcon
          : solid()[label],
        {
          id,
          className: styles.svg,
          width,
          height: width,
          style: IconStyle,
        }
      )}
    </span>
  );

  return !tooltip ? renderIcon() : (
    <Tooltip content={tooltip} position={tooltipPosition}>
      {renderIcon()}
    </Tooltip>
  )
});

Icon.displayName = 'Icon';

Icon.propTypes = {
  onClick: PropTypes.func,
  onKeyPress: PropTypes.func,
  className: PropTypes.string,
  width: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  fill: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]),
  style: PropTypes.shape({}),
  theme: PropTypes.oneOf(['thin', 'solid', 'regular', 'brand']),
  size: PropTypes.oneOf(['huge', 'big', 'medium', 'small']),
  label: PropTypes.string,
  isButton: PropTypes.bool,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  tooltip: PropTypes.any,
  tooltipPosition: PropTypes.string,
  activeFill: PropTypes.string
};

Icon.defaultProps = {
  onClick: () => {},
  onKeyPress: () => {},
  className: undefined,
  style: undefined,
  fill: '#003057',
  width: 16,
  theme: 'thin',
  size: 'medium',
  isButton: false,
  disabled: false,
  label: null,
  active: false,
  activeFill: '#00ccff'
};

export default memo(Icon);
